import { CreatePointForm, PointFieldErrors } from "points/models";

// Client-side validation for the add-a-point form.
//
// This is a courtesy, not a gate. OrderApi validates the same rules on the way in and its answer
// is the one that decides whether a row is written -- these checks exist so the user hears about
// an empty city or a coordinate in the wrong ocean before a round trip, not because the browser
// is trusted. Anything rejected here would be rejected there too.
//
// Kept free of React so the rules can be unit tested directly.

/**
 * Bounding box for North America, generous on every side: Panama up past the north coast of
 * Greenland, the western Aleutians across to Newfoundland.
 *
 * The point is not to draw a precise border. It is to catch the two coordinates that are
 * obviously not a rail yard: 0/0, and a latitude and longitude typed in the wrong order (a
 * US point reversed lands in the Pacific south of nowhere, well outside this box).
 */
export const NORTH_AMERICA_BOUNDS = {
  minLatitude: 14,
  maxLatitude: 84,
  minLongitude: -172,
  maxLongitude: -52,
};

/** Radius the nearby lookup uses. Matches the BFF default; sent explicitly so it is visible. */
export const NEARBY_RADIUS_METERS = 1000;

/** Ceiling on the nearby list. Matches the BFF default. */
export const NEARBY_LIMIT = 25;

/**
 * How far from its hub a point may sit.
 *
 * Not a display choice -- it mirrors geo.CreateClientPoint, which looks for the nearest active
 * hub that lists this client within 100 miles and gives up beyond that. A point outside every
 * hub's radius gets no ClientPointToHub row, so it has no PointOfServiceID, and a trip references
 * PointOfServiceIDs. It would save and then be undispatchable.
 *
 * Enforced here so the operator cannot pick such a place at all, rather than discovering it after
 * saving. The server still decides -- it searches EVERY hub serving the client, not just the one
 * chosen on screen, so it can only ever be more permissive than this.
 */
export const HUB_RADIUS_MILES = 100;

const EARTH_RADIUS_MILES = 3958.7613;
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Great-circle distance in miles.
 *
 * Haversine rather than the flat approximation used for the nearby bounding box: that one only
 * has to be roughly right over a kilometre, this one decides whether a location is allowed at a
 * hundred miles, where the curvature is worth several miles.
 */
export const distanceMiles = (
  fromLatitude: number,
  fromLongitude: number,
  toLatitude: number,
  toLongitude: number
): number => {
  const dLat = toRadians(toLatitude - fromLatitude);
  const dLon = toRadians(toLongitude - fromLongitude);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(fromLatitude)) * Math.cos(toRadians(toLatitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
};

/** True when a coordinate is close enough to the hub to be servable from it. */
export const isWithinHubRadius = (
  hub: { latitude: number; longitude: number } | null | undefined,
  latitude: number,
  longitude: number
): boolean => {
  if (!hub || !Number.isFinite(hub.latitude) || !Number.isFinite(hub.longitude)) return true;
  return distanceMiles(hub.latitude, hub.longitude, latitude, longitude) <= HUB_RADIUS_MILES;
};

export const isWithinNorthAmerica = (latitude: number, longitude: number): boolean =>
  latitude >= NORTH_AMERICA_BOUNDS.minLatitude &&
  latitude <= NORTH_AMERICA_BOUNDS.maxLatitude &&
  longitude >= NORTH_AMERICA_BOUNDS.minLongitude &&
  longitude <= NORTH_AMERICA_BOUNDS.maxLongitude;

/**
 * True for the placeholder coordinate.
 *
 * Roughly 3,376 client points already sit at exactly 0/0 -- open water in the Gulf of Guinea --
 * because nothing ever checked. It is the single most common bad value in the table and it gets
 * its own message rather than being folded into the bounds check, because "outside North
 * America" does not tell someone who left the field alone what went wrong.
 */
export const isNullIsland = (latitude: number, longitude: number): boolean =>
  latitude === 0 && longitude === 0;

/** Parses a coordinate the user typed. Returns null for anything that is not a finite number. */
export const parseCoordinate = (value: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "-" || trimmed === "." || trimmed === "-.") return null;
  if (!/^-?\d*\.?\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const STATE_ABBREVIATION = /^[A-Za-z]{2}$/;

export const validateCreatePoint = (
  form: CreatePointForm,
  hub?: { latitude: number; longitude: number } | null
): PointFieldErrors => {
  const errors: PointFieldErrors = {};

  if (!form.clientId) {
    errors.clientId = "Choose the client this location belongs to.";
  }

  if (!form.hubId) {
    errors.hubId = "Choose the hub this location is served from.";
  }

  if (!form.clientPointName?.trim()) {
    errors.clientPointName = "Name is required.";
  }

  if (!form.addressLine1?.trim()) {
    errors.addressLine1 = "Address is required.";
  }

  if (!form.city?.trim()) {
    errors.city = "City is required.";
  }

  if (!form.stateAbbreviation?.trim()) {
    errors.stateAbbreviation = "State is required.";
  } else if (!STATE_ABBREVIATION.test(form.stateAbbreviation.trim())) {
    errors.stateAbbreviation = "Use the two-letter state or province abbreviation.";
  }

  const latitude = parseCoordinate(form.latitude);
  const longitude = parseCoordinate(form.longitude);

  if (latitude === null) {
    errors.latitude = "Latitude is required.";
  }

  if (longitude === null) {
    errors.longitude = "Longitude is required.";
  }

  if (latitude !== null && longitude !== null) {
    if (isNullIsland(latitude, longitude)) {
      // Deliberately blunt. This is the value the screen exists to stop.
      errors.latitude = "0 / 0 is not a location. Search an address or drag the pin to the real spot.";
    } else if (!isWithinNorthAmerica(latitude, longitude)) {
      errors.latitude = `That coordinate is outside North America (${latitude}, ${longitude}). Check the two values are not swapped.`;
    }
  }

  if (!form.isConfirmed) {
    errors.isConfirmed = "Confirm the pin is in the right place before saving.";
  }

  return errors;
};

export const hasErrors = (errors: PointFieldErrors): boolean => Object.keys(errors).length > 0;

/**
 * Turns an ASP.NET ValidationProblemDetails `errors` bag into the form's own error shape.
 *
 * The keys arrive named after the request DTO's properties, which model binding may report in
 * either case ("ClientPointName" or "clientPointName"), so they are lower-cased on the first
 * character to match the form's field names. A key the form has no field for is kept under its
 * own name; the screen shows those separately rather than dropping them, because a validation
 * message with nowhere to land is still something the user needs to read.
 */
export const mapApiFieldErrors = (apiErrors: Record<string, string[] | string>): PointFieldErrors => {
  const mapped: PointFieldErrors = {};
  if (!apiErrors) return mapped;

  Object.keys(apiErrors).forEach((key) => {
    if (!key) return;
    const value = apiErrors[key];
    const message = Array.isArray(value) ? value.join(" ") : String(value);
    if (!message) return;

    const field = key.charAt(0).toLowerCase() + key.slice(1);
    mapped[field] = message;
  });

  return mapped;
};

/**
 * Turns OrderApi's own `RuleError` list into the form's error shape.
 *
 * The create endpoint answers a rejection with its `CreatePointResult`, whose `errors` is a LIST
 * of { ruleName, message } -- not the `ValidationProblemDetails` dictionary that
 * mapApiFieldErrors above expects. Passing the list to that function silently produces
 * { "0": "[object Object]" }, so the messages have to be read as what they are.
 *
 * RuleName carries the request property name, lower-cased on the first character to match the
 * form's own field names. More than one rule can fail on the same field, so messages are joined
 * rather than overwritten; a rule with no name is kept under a synthetic key so the screen still
 * shows it rather than dropping it.
 */
export const mapRuleErrors = (ruleErrors: any[]): PointFieldErrors => {
  const mapped: PointFieldErrors = {};
  if (!Array.isArray(ruleErrors)) return mapped;

  ruleErrors.forEach((error, index) => {
    const message = String(error?.message ?? "").trim();
    if (!message) return;

    const ruleName = String(error?.ruleName ?? "").trim();
    const field = ruleName ? ruleName.charAt(0).toLowerCase() + ruleName.slice(1) : `error${index}`;
    mapped[field] = mapped[field] ? `${mapped[field]} ${message}` : message;
  });

  return mapped;
};

/**
 * Reads the field errors out of a 400 body, whichever of the two shapes it is.
 *
 * A create can be rejected in two different places. OrderApi's own validator answers with a
 * CreatePointResult carrying a RuleError LIST; ASP.NET model binding rejects a malformed request
 * before the controller ever runs and answers with a ValidationProblemDetails DICTIONARY. Both
 * are 400s and both carry messages the user needs against their fields.
 */
export const mapCreatePointErrors = (data: any): PointFieldErrors => {
  if (!data) return {};
  return Array.isArray(data.errors) ? mapRuleErrors(data.errors) : mapApiFieldErrors(data.errors);
};

/** Metres, rendered for people who work in miles but need to see close distances honestly. */
export const formatDistance = (meters: number): string => {
  if (typeof meters !== "number" || !Number.isFinite(meters)) return "--";
  const miles = meters / 1609.344;
  if (meters < 1000) {
    return `${Math.round(meters)} m (${miles.toFixed(2)} mi)`;
  }
  return `${(meters / 1000).toFixed(2)} km (${miles.toFixed(1)} mi)`;
};
